import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { FloatingButton } from './FloatingButton';
import { WelcomeTeaser } from './WelcomeTeaser';
import { ChatWindow } from './ChatWindow';
import { useChat } from '@/hooks/useChat';
import { getOrCreateSessionId, getSavedEmail, saveEmail } from '@/services/session';
import { createStateSyncService, StateSyncService } from '@/services/stateSync';
import type { WidgetConfig, WidgetSyncState } from '@/types';

interface AppProps {
  config: WidgetConfig;
  /** Initial open state (used in preview mode) */
  initialOpen?: boolean;
}

export function App({ config, initialOpen = false }: AppProps) {
  const isEmbedded = config.mode === 'embedded';

  const [isOpen, setIsOpen] = useState(initialOpen || isEmbedded);
  const [isVisible, setIsVisible] = useState(true);
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const [hasOpened, setHasOpened] = useState(initialOpen || isEmbedded);
  const [isAdaptingTheme, setIsAdaptingTheme] = useState(false);

  // Use external sessionId if provided (for sync), otherwise generate
  const sessionId = getOrCreateSessionId(config.sessionId);
  const [userEmail, setUserEmail] = useState<string | null>(() => getSavedEmail());

  // Sync service ref
  const syncServiceRef = useRef<StateSyncService | null>(null);

  // State change handler for sync
  const handleStateChange = useCallback((state: WidgetSyncState) => {
    if (syncServiceRef.current && config.enableSync) {
      syncServiceRef.current.emitStateUpdate(state);
    }
  }, [config.enableSync]);

  const chat = useChat({
    config,
    sessionId,
    userEmail,
    onStateChange: config.enableSync ? handleStateChange : undefined,
  });

  // Initialize sync service
  useEffect(() => {
    if (!config.enableSync || !config.instanceId) return;

    const service = createStateSyncService({
      instanceId: config.instanceId,
      sessionId,
      parentOrigin: config.parentOrigin || '*',
      onStateSync: (state: WidgetSyncState) => {
        chat.syncState({
          messages: state.messages,
          inputText: state.inputText,
          isAwaitingResponse: state.isAwaitingResponse,
          followUpQuestions: state.followUpQuestions,
        });
      },
      onVisibilityChange: (action) => {
        // Dispatch visibility event (same as public API)
        document.dispatchEvent(new CustomEvent('widget-visibility', {
          detail: { action },
          bubbles: true,
        }));
      },
    });

    syncServiceRef.current = service;

    return () => {
      service.destroy();
      syncServiceRef.current = null;
    };
  }, [config.enableSync, config.instanceId, config.parentOrigin, sessionId, chat.syncState]);

  // Listen for external state toggle events (from preview mode postMessage)
  useEffect(() => {
    if (!config.isPreviewMode) return;

    const handleStateToggle = (event: Event) => {
      const customEvent = event as CustomEvent<{ state: 'open' | 'closed' }>;
      const newState = customEvent.detail?.state;
      if (newState === 'open') {
        setIsOpen(true);
        setHasOpened(true);
      } else if (newState === 'closed') {
        setIsOpen(false);
      }
    };

    // Listen on document since events bubble from shadow root
    document.addEventListener('widget-state-toggle', handleStateToggle);
    return () => {
      document.removeEventListener('widget-state-toggle', handleStateToggle);
    };
  }, [config.isPreviewMode]);

  // Listen for visibility control events (from public API)
  useEffect(() => {
    if (isEmbedded) return; // No visibility control in embedded mode

    const handleVisibility = (event: Event) => {
      const customEvent = event as CustomEvent<{ action: 'show' | 'hide' | 'open' | 'close' }>;
      const action = customEvent.detail?.action;
      switch (action) {
        case 'show':
          setIsVisible(true);
          break;
        case 'hide':
          setIsVisible(false);
          setIsOpen(false);
          break;
        case 'open':
          setIsOpen(true);
          setHasOpened(true);
          break;
        case 'close':
          setIsOpen(false);
          break;
      }
    };

    document.addEventListener('widget-visibility', handleVisibility);
    return () => {
      document.removeEventListener('widget-visibility', handleVisibility);
    };
  }, [isEmbedded]);

  // Listen for theme adaptation events (first-time installation)
  useEffect(() => {
    if (config.isPreviewMode) return;

    const handleAdapting = () => {
      setIsAdaptingTheme(true);
      // Show teaser immediately when adapting starts
      if (!teaserDismissed && !isOpen) {
        setShowTeaser(true);
      }
    };

    const handleAdapted = (event: Event) => {
      setIsAdaptingTheme(false);
      const customEvent = event as CustomEvent<{ success: boolean; adminUrl?: string }>;
      if (customEvent.detail?.success && customEvent.detail?.adminUrl) {
        chat.addSystemMessage(
          `We've adapted the widget to match your site's design. Want to customize it further? Visit your [widget settings](${customEvent.detail.adminUrl}/widget-design).`
        );
      }
    };

    document.addEventListener('widget-theme-adapting', handleAdapting);
    document.addEventListener('widget-theme-adapted', handleAdapted);
    return () => {
      document.removeEventListener('widget-theme-adapting', handleAdapting);
      document.removeEventListener('widget-theme-adapted', handleAdapted);
    };
  }, [config.isPreviewMode, chat.addSystemMessage, teaserDismissed, isOpen]);

  // Show teaser after delay (only once)
  useEffect(() => {
    if (teaserDismissed || isOpen) return;

    const timer = setTimeout(() => {
      setShowTeaser(true);
    }, config.teaserDelay);

    return () => clearTimeout(timer);
  }, [config.teaserDelay, teaserDismissed, isOpen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setShowTeaser(false);
    setHasOpened(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleTeaserDismiss = useCallback(() => {
    setShowTeaser(false);
    setTeaserDismissed(true);
  }, []);

  const handleEmailSubmit = useCallback(
    async (questionId: string, email: string) => {
      await chat.submitEmail(questionId, email);
      saveEmail(email);
      setUserEmail(email);
    },
    [chat.submitEmail],
  );

  // Extract settings with defaults
  const settings = config.settings;
  const teaserEnabled = settings?.welcomeTeaser?.enabled ?? true;
  const teaserText = settings?.welcomeTeaser?.text;
  const headerText = settings?.headerText;
  const inputPlaceholder = settings?.inputPlaceholder;

  // In embedded mode, always render the chat window
  if (isEmbedded) {
    return (
      <ChatWindow
        messages={chat.messages}
        isLoading={chat.isLoading}
        error={chat.error}
        isRateLimited={chat.isRateLimited}
        onSendMessage={chat.sendMessage}
        onRate={chat.submitRating}
        onEmailSubmit={handleEmailSubmit}
        onClearError={chat.clearError}
        onClose={handleClose}
        suggestedQuestions={config.suggestedQuestions}
        followUpQuestions={chat.followUpQuestions}
        hasSentMessage={chat.hasSentMessage}
        headerText={headerText}
        inputPlaceholder={inputPlaceholder}
        embedded={true}
        inputValue={chat.inputText}
        onInputChange={chat.setInputText}
      />
    );
  }

  // Floating mode: render button, teaser, and chat window based on state
  return (
    <>
      {isVisible && <FloatingButton onClick={handleOpen} isOpen={isOpen} />}

      {isVisible && teaserEnabled && showTeaser && !isOpen && (
        <WelcomeTeaser
          onOpen={handleOpen}
          onDismiss={handleTeaserDismiss}
          isAdapting={isAdaptingTheme}
          text={teaserText}
        />
      )}

      {/* Lazy render: only mount ChatWindow after first open */}
      {hasOpened && isOpen && (
        <ChatWindow
          messages={chat.messages}
          isLoading={chat.isLoading}
          error={chat.error}
          isRateLimited={chat.isRateLimited}
          onSendMessage={chat.sendMessage}
          onRate={chat.submitRating}
          onEmailSubmit={handleEmailSubmit}
          onClearError={chat.clearError}
          onClose={handleClose}
          suggestedQuestions={config.suggestedQuestions}
          followUpQuestions={chat.followUpQuestions}
          hasSentMessage={chat.hasSentMessage}
          headerText={headerText}
          inputPlaceholder={inputPlaceholder}
          inputValue={chat.inputText}
          onInputChange={chat.setInputText}
        />
      )}
    </>
  );
}
